import { useMemo, type ReactNode } from 'react';
import {
  Button,
  Dropdown,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add, Draggable, TrashCan } from '@carbon/react/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { NumericField } from './numeric-field';
import type { CurveOption, Leg } from './types';
import styles from './leg-composition-editor.module.scss';

// A leg picker option: a real curve (numeric id) or the CONSTANT pseudo-curve (null value).
interface LegOption {
  value: number | null;
  label: string;
}

interface LegCompositionEditorProps {
  legs: Leg[];
  curveOptions: CurveOption[]; // real curves; the CONSTANT pseudo-curve is appended automatically
  onChange: (legs: Leg[]) => void;
  formulaLabel?: string;
  formulaName: string;
  readOnly?: boolean; // detail view mode — render legs inert (no controls, add, or remove)
  hideFormula?: boolean; // caller renders the combined formula in its own section
}

const fmt = (n: number) => String(n);

function legExpression(
  leg: Leg,
  nameOf: (curveId: number | null) => string
): string {
  if (leg.curveId === null) return fmt(leg.multiplier || 0);
  const name = nameOf(leg.curveId);
  return leg.multiplier === 1 ? name : `${name} × ${fmt(leg.multiplier)}`;
}

function combinedFormula(
  legs: Leg[],
  nameOf: (curveId: number | null) => string
): string {
  if (!legs.length) return '0';
  return legs.map((l) => `(${legExpression(l, nameOf)})`).join(' + ');
}

// Live "Name = (legA) + (legB)" for callers that render the formula in their own section
// (e.g. the contract editor's Combined formula card).
export function combinedFormulaText(
  legs: Leg[],
  curveOptions: CurveOption[],
  formulaName: string
): string {
  const nameOf = (curveId: number | null) => {
    if (curveId === null) return 'Constant';
    return (
      curveOptions.find((o) => o.value === curveId)?.label ??
      `curve #${curveId}`
    );
  };
  return `${formulaName || 'Contract'} = ${combinedFormula(legs, nameOf)}`;
}

// Stable module-level array so DndContext doesn't see a new `modifiers` reference every render.
const DND_MODIFIERS = [restrictToVerticalAxis];

// A drag-to-reorder leg row. The leading "#" cell doubles as the drag handle so the row's form
// controls stay clickable; the remaining cells pass through as children.
function SortableLegRow({
  id,
  index,
  children,
}: {
  id: string;
  index: number;
  children: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <TableCell className={styles.idx}>
        <button
          type="button"
          className={styles.dragHandle}
          aria-label="Drag to reorder leg"
          {...attributes}
          {...listeners}
        >
          <Draggable />
          <span>{String(index + 1).padStart(2, '0')}</span>
        </button>
      </TableCell>
      {children}
    </TableRow>
  );
}

// The curve × multiplier leg table (CONSTANT pseudo-curve, ordered sum, live combined formula).
// An inline-editable line-item grid (the LineItemEntry pattern): a Carbon DataTable whose cells
// hold form controls, with drag-to-reorder rows, an Add-row action and per-row delete. Rows stay
// editable because the parent page persists everything through a single Save. Reused by
// curve-management's derived sections and the pricing-contract builder.
export function LegCompositionEditor({
  legs,
  curveOptions,
  onChange,
  formulaLabel = 'Combined curve formula',
  formulaName,
  readOnly = false,
  hideFormula = false,
}: LegCompositionEditorProps) {
  const options: LegOption[] = [
    ...curveOptions,
    { value: null, label: 'Constant' },
  ];

  const update = (index: number, patch: Partial<Leg>) =>
    onChange(legs.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const remove = (index: number) =>
    onChange(
      legs.filter((_, i) => i !== index).map((l, i) => ({ ...l, legId: i + 1 }))
    );

  const add = () =>
    onChange([
      ...legs,
      {
        legId: legs.length + 1,
        curveId: curveOptions[0]?.value ?? null,
        multiplier: 1,
      },
    ]);

  const optionFor = (value: number | null) =>
    options.find((o) => o.value === value) ?? options[0];

  const nameOf = (curveId: number | null) => {
    if (curveId === null) return 'Constant';
    return (
      options.find((o) => o.value === curveId)?.label ?? `curve #${curveId}`
    );
  };

  // Drag-to-reorder for the leg rows (same interaction as the transformation editor). A small
  // activation distance keeps clicks on the row's form controls from starting a drag. Rows are
  // identified by legId (unique per composition); on drop we reorder and re-number legId so the
  // persisted order (leg_id) matches the new arrangement.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const legIds = useMemo(() => legs.map((l) => String(l.legId)), [legs]);
  const handleReorder = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = legs.findIndex((l) => String(l.legId) === active.id);
    const to = legs.findIndex((l) => String(l.legId) === over.id);
    if (from === -1 || to === -1) return;
    onChange(arrayMove(legs, from, to).map((l, i) => ({ ...l, legId: i + 1 })));
  };

  // The non-index cells of a leg row (shared by the read-only and editable renders).
  const legCells = (leg: Leg, i: number) => (
    <>
      <TableCell>
        {readOnly ? (
          nameOf(leg.curveId)
        ) : (
          <Dropdown<LegOption>
            id={`leg-curve-${i}`}
            size="sm"
            label="Curve"
            titleText=""
            hideLabel
            autoAlign
            items={options}
            itemToString={(o) => o?.label ?? ''}
            selectedItem={optionFor(leg.curveId)}
            onChange={({ selectedItem }) =>
              selectedItem && update(i, { curveId: selectedItem.value })
            }
          />
        )}
      </TableCell>
      <TableCell>
        {readOnly ? (
          leg.multiplier
        ) : (
          <NumericField
            id={`leg-mult-${i}`}
            size="sm"
            label="Multiplier"
            hideLabel
            value={leg.multiplier}
            onValueChange={(value) => update(i, { multiplier: value })}
          />
        )}
      </TableCell>
      <TableCell className={styles.resolved}>
        {legExpression(leg, nameOf)}
      </TableCell>
      {!readOnly && (
        <TableCell className={styles.actionCol}>
          <Button
            hasIconOnly
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            iconDescription="Remove leg"
            onClick={() => remove(i)}
          />
        </TableCell>
      )}
    </>
  );

  const table = (
    <Table size="sm" useZebraStyles={false}>
      <TableHead>
        <TableRow>
          <TableHeader className={styles.numCol}>#</TableHeader>
          <TableHeader>Curve</TableHeader>
          <TableHeader className={styles.multCol}>Multiplier</TableHeader>
          <TableHeader>Resolved series</TableHeader>
          {!readOnly && <TableHeader className={styles.actionCol} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {legs.length === 0 ? (
          <TableRow>
            <TableCell colSpan={readOnly ? 4 : 5} className={styles.empty}>
              {readOnly
                ? 'No legs.'
                : 'No legs yet. Add a leg to build this curve.'}
            </TableCell>
          </TableRow>
        ) : readOnly ? (
          legs.map((leg, i) => (
            <TableRow key={leg.legId}>
              <TableCell className={styles.idx}>
                {String(i + 1).padStart(2, '0')}
              </TableCell>
              {legCells(leg, i)}
            </TableRow>
          ))
        ) : (
          <SortableContext
            items={legIds}
            strategy={verticalListSortingStrategy}
          >
            {legs.map((leg, i) => (
              <SortableLegRow key={leg.legId} id={String(leg.legId)} index={i}>
                {legCells(leg, i)}
              </SortableLegRow>
            ))}
          </SortableContext>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div>
      <TableContainer>
        {readOnly ? (
          table
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={DND_MODIFIERS}
            onDragEnd={handleReorder}
          >
            {table}
          </DndContext>
        )}
      </TableContainer>
      {!readOnly && (
        <div className={styles.addRow}>
          <Button kind="ghost" size="sm" renderIcon={Add} onClick={add}>
            Add leg
          </Button>
        </div>
      )}
      {!hideFormula && (
        <div className={styles.formula}>
          <span className={styles.formulaLabel}>{formulaLabel}</span>
          <span className={styles.formulaHint}>Legs summed in order</span>
          <div className={styles.formulaBody}>
            {formulaName} = {combinedFormula(legs, nameOf)}
          </div>
        </div>
      )}
    </div>
  );
}
